/**
 * DNS (servidor) — V9.0.2 (22/10/2026): SEMPRE 1 DNS, a da LISTA do cliente.
 *   perfil assistindo → conta → lista atribuída à conta (Gerenciador → Listas) → DNS da lista.
 * Acabou a lista de várias DNS (tabela global `server_dns`, reservas por lista). Nada fixo no código.
 */

export function normalizeServerUrl(s: string): string {
  const t = String(s || '').trim().replace(/\/+$/, '');
  if (!t) return '';
  return t.startsWith('http://') || t.startsWith('https://') ? t : `http://${t}`;
}

/** Troca o servidor (protocolo + host) do endereço pelo servidor indicado. Sem servidor: devolve o original. */
export function withServerHost(rawUrl: string, serverUrl: string): string {
  if (!rawUrl || !serverUrl) return rawUrl;
  try {
    const urlObj = new URL(rawUrl);
    const dnsObj = new URL(serverUrl);
    urlObj.protocol = dnsObj.protocol;
    urlObj.host = dnsObj.host;
    return urlObj.toString();
  } catch {
    return rawUrl;
  }
}

// DNS da lista da conta guardada para quem não está dentro do React (guia de programação). O IPTVContext atualiza.
const LIST_DNS_KEY = 'opentv_list_dns';
let currentListServer = '';

export function setCurrentListServer(url: string): void {
  currentListServer = url || '';
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LIST_DNS_KEY, currentListServer);
  } catch {}
}

/** A DNS da lista da conta ('' se ainda não há lista no aparelho). */
export function getCurrentListServer(): string {
  if (currentListServer) return currentListServer;
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(LIST_DNS_KEY)) || '';
  } catch {
    return '';
  }
}

/**
 * V9.0.2 — Servidor (DNS) vem da LISTA do cliente (regra do dono do sistema, 22/10/2026):
 *   perfil assistindo → conta → lista atribuída à conta (Gerenciador → Listas) → DNS da lista.
 * SEMPRE 1 DNS por lista (sem reservas). A tabela global de DNS do Gerenciador (`server_dns`) NÃO é usada
 * para tocar nem para baixar a lista. Nada fixo no código. Sem lista no aparelho: '' e o item mantém o
 * próprio endereço.
 */
export interface ListServerLike {
  id?: string;
  type?: string;
  serverUrl?: string;
}

/** A DNS de uma lista (só Xtream; lista M3U guarda o endereço do arquivo, não o do vídeo → ''). */
export function getServerOfList(pl?: ListServerLike | null): string {
  if (!pl || pl.type !== 'xtream') return '';
  const n = normalizeServerUrl(String(pl.serverUrl || ''));
  if (!n) return '';
  try {
    return new URL(n).origin;
  } catch {
    return n;
  }
}

/**
 * A DNS para tocar um item: a da lista de onde o item veio (`playlistId`); se o item não diz a lista
 * (ou ela não está no aparelho), a da lista atribuída à conta; por último a 1ª lista do aparelho que tenha DNS.
 */
export function getListServer(
  lists: ListServerLike[] | null | undefined,
  playlistId?: string | null,
  assignedPlaylistId?: string | null
): string {
  const all = (Array.isArray(lists) ? lists : []).filter(Boolean) as ListServerLike[];
  const byId = (id?: string | null) => (id ? all.find((p) => String(p.id) === String(id)) : undefined);
  // Item de uma lista conhecida: vale a DNS dela (lista M3U → '' e o item mantém o próprio endereço)
  const own = byId(playlistId);
  if (own) return getServerOfList(own);
  return (
    getServerOfList(byId(assignedPlaylistId)) ||
    all.map((p) => getServerOfList(p)).find(Boolean) ||
    ''
  );
}

/**
 * V9.0.6 — CONTA (usuário/senha) para tocar DIRETO = a conta que o servidor manda junto com a lista
 * (load_all: conta ativa do pool da lista; conta excluída/vencida não vem). Os endereços do catálogo guardam
 * a conta de quando a lista foi baixada — tocando direto com conta antiga o servidor responde 404.
 * Mesma escolha de lista do getListServer.
 */
export interface ListAccount {
  user: string;
  pass: string;
}

export function getListAccount(
  lists: Array<ListServerLike & { username?: string; password?: string }> | null | undefined,
  playlistId?: string | null,
  assignedPlaylistId?: string | null
): ListAccount | null {
  const all = (Array.isArray(lists) ? lists : []).filter(Boolean) as Array<ListServerLike & { username?: string; password?: string }>;
  const byId = (id?: string | null) => (id ? all.find((p) => String(p.id) === String(id)) : undefined);
  const acc = (pl?: { username?: string; password?: string } | null): ListAccount | null =>
    pl && pl.username && pl.password ? { user: String(pl.username), pass: String(pl.password) } : null;
  const own = byId(playlistId);
  if (own) return acc(own);
  return acc(byId(assignedPlaylistId)) || all.map((p) => acc(p)).find(Boolean) || null;
}

/** Troca usuário/senha do endereço (Xtream) pela conta da lista. Sem conta: devolve o original. */
export function withListAccount(rawUrl: string, acc?: ListAccount | null): string {
  if (!rawUrl || !acc || !acc.user || !acc.pass) return rawUrl;
  const u = encodeURIComponent(acc.user);
  const p = encodeURIComponent(acc.pass);
  return rawUrl
    .replace(/^(https?:\/\/[^\/]+\/(?:live|movie|series|timeshift)\/)[^\/]+\/[^\/]+\//i, `$1${u}/${p}/`)
    .replace(
      /^(https?:\/\/[^\/]+\/)(?!live\/|movie\/|series\/|timeshift\/|hls\/|player_api|xmltv|get\.php)[^\/]+\/[^\/]+(\/\d+(?:\.[a-z0-9]+)?(?:[?#].*)?)$/i,
      `$1${u}/${p}$2`
    )
    .replace(/([?&]username=)[^&]*/i, `$1${u}`)
    .replace(/([?&]password=)[^&]*/i, `$1${p}`);
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
// ANTIGO (várias DNS do Gerenciador, tabela `server_dns`) — o Web NÃO usa mais (V9.0.2).
// Fica SÓ porque o Stick (tvbox-stick: LivePlayer, VodPlayer, IPTVContext, fastestDns) ainda importa daqui.
// APAGAR quando a conversa ACESSO AO STICK passar o Stick para getListServer (1 DNS, a da lista).
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export interface ManagerDnsLike {
  url?: string;
  isActive?: boolean;
  priority?: number;
}

const DNS_STORAGE_KEY = 'nexus_system_dns_list';

function readSavedList(): ManagerDnsLike[] {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(DNS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** ANTIGO (Stick): servidores ATIVOS do Gerenciador, na ordem de prioridade. */
export function getManagerDnsUrls(list?: ManagerDnsLike[] | null): string[] {
  const source = list && list.length > 0 ? list : readSavedList();
  const seen = new Set<string>();
  return source
    .filter((d) => d && d.isActive && d.url && String(d.url).trim())
    .slice()
    .sort((a, b) => (a.priority || 99) - (b.priority || 99))
    .map((d) => normalizeServerUrl(String(d.url)))
    .filter((u) => {
      if (!u || seen.has(u)) return false;
      seen.add(u);
      return true;
    });
}

/** ANTIGO (Stick): primeiro servidor do Gerenciador ('' se não houver). */
export function getPrimaryManagerDns(list?: ManagerDnsLike[] | null): string {
  return getManagerDnsUrls(list)[0] || '';
}

/** ANTIGO (Stick): Gerenciador primeiro, depois os extras, sem repetição. */
export function buildServerCandidates(extra: Array<string | undefined | null> = [], list?: ManagerDnsLike[] | null): string[] {
  const seen = new Set<string>();
  return [...getManagerDnsUrls(list), ...extra.map((s) => normalizeServerUrl(String(s || '')))].filter((u) => {
    if (!u || seen.has(u)) return false;
    seen.add(u);
    return true;
  });
}
